import React, { useState, forwardRef } from 'react';
import { Tabs } from '@cloudscape-design/components';
import { ControlFilterView } from 'src/components/Filter';
import { BulkEditModal } from 'src/components/BulkEdit/BulkEditModal';
import { GridRef } from './ControlSummaryPageAGGrid';
import { ControlId } from '@amzn/controllershiphub-typescript-client';
import './ControlDataTabContent.scss';

interface ControlDataTabContentProps {
  controlId: string;
  isAdditionalDataEnabled: boolean;
  loadDataByDefault: boolean;
  controlAttributesData: any;
  controlDatasetKey: string;
  selectedRows: any[];
  isBulkEditModalVisible: boolean;
  onSelectionChange: (rows: any[]) => void;
  onBulkEditModalVisibility: (visible: boolean) => void;
  onBulkEditSuccess: (updatedRecords: any[]) => void;
  activeSubTabId: string;
  setActiveSubTabId: (tabId: string) => void;
  onNavigateToExports?: () => void;
  onOpenPOExport?: (activeFilters: Record<string, string[]>) => void;
}

export const ControlDataTabContent = forwardRef<GridRef, ControlDataTabContentProps>(
  (
    {
      controlId,
      isAdditionalDataEnabled,
      loadDataByDefault,
      controlAttributesData,
      controlDatasetKey,
      selectedRows,
      isBulkEditModalVisible,
      onSelectionChange,
      onBulkEditModalVisibility,
      onBulkEditSuccess,
      activeSubTabId,
      setActiveSubTabId,
      onNavigateToExports,
      onOpenPOExport,
    },
    ref
  ) => {
    const renderDataContent = (dataType: 'GL' | 'PO' | 'DEFAULT') => {
      // For PO data, try controlid_additional first, but have fallback logic
      const effectiveControlId = dataType === 'PO' ? `${controlId}openpo` : controlId;

      return (
        <div style={{ margin: '-8px 0 0 0', padding: 0 }}>
          <ControlFilterView
            controlId={effectiveControlId}
            ref={ref}
            onSelectionChange={onSelectionChange}
            dataType={dataType}
            loadDataByDefault={loadDataByDefault}
            additionalDataEnabled={isAdditionalDataEnabled}
            fallbackControlId={dataType === 'PO' ? controlId : undefined} // Fallback to main control for PO
            onNavigateToExports={onNavigateToExports}
            onOpenPOExport={onOpenPOExport}
          />
          {controlAttributesData?.attributesMetadata && (
            <BulkEditModal
              visible={isBulkEditModalVisible}
              onDismiss={() => onBulkEditModalVisibility(false)}
              selectedRows={selectedRows}
              attributesMetadata={controlAttributesData.attributesMetadata}
              controlId={effectiveControlId as unknown as ControlId}
              controlDatasetKey={controlDatasetKey}
              onSaveSuccess={onBulkEditSuccess}
              clearGridSelection={() => {
                if (ref && typeof ref === 'object' && ref.current?.api) {
                  ref.current.api.deselectAll();
                }
              }}
            />
          )}
        </div>
      );
    };

    if (isAdditionalDataEnabled && controlId === 'grc18956') {
      return (
        <div className="control-data-nested-tabs">
          <Tabs
            activeTabId={activeSubTabId}
            onChange={({ detail }) => setActiveSubTabId(detail.activeTabId)}
            tabs={[
              {
                label: 'GL Data',
                id: 'gl-data',
                content: renderDataContent('GL'),
              },
              {
                label: 'Open PO Data',
                id: 'open-po-data',
                content: renderDataContent('PO'),
              },
            ]}
          />
        </div>
      );
    }

    // If additionalDataEnabled is false, show the default control data
    return <div className="control-data-single-content">{renderDataContent('DEFAULT')}</div>;
  }
);
